import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Bell, Save, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import complianceApi from "../../api/complianceApi";
import { toast } from "react-toastify";

const COMPLIANCE_CATEGORIES = [
  { value: "GST", label: "GST Compliance" },
  { value: "TDS", label: "TDS Compliance" },
  { value: "INCOME_TAX", label: "Income Tax" },
  { value: "LEGAL", label: "Legal Compliance" },
  { value: "INTERNAL_ACCOUNTING", label: "Internal Accounting" },
];

const STATUS_OPTIONS = [
  { key: "notifyOnPending", label: "Pending", description: "When document is uploaded" },
  { key: "notifyOnVerified", label: "Verified", description: "When document is verified" },
  { key: "notifyOnDisputed", label: "Disputed", description: "When there's an issue" },
  { key: "notifyOnBooked", label: "Booked", description: "When entered in books" },
  { key: "notifyOnFiled", label: "Filed", description: "When successfully filed" },
];

const GlobalNotificationSettings = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await complianceApi.getAllGlobalNotificationSettings(token);
      const settingsMap = {};
      
      if (response && response.data) {
        response.data.forEach((setting) => {
          settingsMap[setting.complianceCategory] = setting;
        });
      }

      // Initialize missing categories with defaults
      COMPLIANCE_CATEGORIES.forEach((cat) => {
        if (!settingsMap[cat.value]) {
          settingsMap[cat.value] = {
            complianceCategory: cat.value,
            notifyOnPending: false,
            notifyOnVerified: true,
            notifyOnDisputed: true,
            notifyOnBooked: false,
            notifyOnFiled: true,
            isEnabled: true,
            defaultCcEmails: "",
            description: "",
          };
        }
      });

      setSettings(settingsMap);
    } catch (error) {
      console.error("Error fetching global settings:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, field) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: !prev[category][field],
      },
    }));
  };

  const handleInputChange = (category, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const promises = Object.values(settings).map((setting) =>
        complianceApi.saveGlobalNotificationSettings(setting, token)
      );

      await Promise.all(promises);
      toast.success("Global notification settings saved successfully");
      fetchSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Bell className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                Global Notification Settings
              </h3>
              <p className="text-sm text-slate-500">
                Configure default email notifications for all accounts
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchSettings}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                <Save size={16} />
              )}
              Save All
            </button>
          </div>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="p-6 space-y-6">
        {COMPLIANCE_CATEGORIES.map((category) => {
          const categorySetting = settings[category.value] || {};
          return (
            <div
              key={category.value}
              className="border border-slate-200 rounded-lg overflow-hidden"
            >
              {/* Category Header */}
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-slate-800">{category.label}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {categorySetting.description || "Configure notification preferences"}
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm text-slate-600">Enable</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={categorySetting.isEnabled || false}
                      onChange={() => handleToggle(category.value, "isEnabled")}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </div>
                </label>
              </div>

              {/* Status Toggles */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {STATUS_OPTIONS.map((status) => (
                    <div
                      key={status.key}
                      className={`p-3 rounded-lg border ${
                        categorySetting[status.key]
                          ? "border-indigo-300 bg-indigo-50"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={categorySetting[status.key] || false}
                          onChange={() => handleToggle(category.value, status.key)}
                          disabled={!categorySetting.isEnabled}
                          className="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-800 text-sm">
                            {status.label}
                          </div>
                          <div className="text-xs text-slate-500">
                            {status.description}
                          </div>
                        </div>
                        {categorySetting[status.key] ? (
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                      </label>
                    </div>
                  ))}
                </div>

                {/* CC Emails */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Default CC Emails (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={categorySetting.defaultCcEmails || ""}
                    onChange={(e) =>
                      handleInputChange(category.value, "defaultCcEmails", e.target.value)
                    }
                    disabled={!categorySetting.isEnabled}
                    placeholder="email1@example.com, email2@example.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GlobalNotificationSettings;
